(() => {
  const TEXT_SAMPLE = "THE QUICK BROWN FOX JUMPS OVER THE LAZY DOG";
  const COMMON_REFRESH_RATES = [30, 50, 60, 75, 90, 100, 120, 144, 165, 180, 200, 240, 260, 280, 300, 320, 360];

  function clamp(value, min, max) {
    const number = Number(value);

    if (Number.isNaN(number)) {
      return min;
    }

    return Math.min(max, Math.max(min, number));
  }

  function normalizeMode(mode) {
    return mode === "standard" ? "standard" : "gaming";
  }

  function getHashMode() {
    const hash = window.location.hash.toLowerCase();

    if (hash.includes("standard")) {
      return "standard";
    }

    if (hash.includes("gaming")) {
      return "gaming";
    }

    return null;
  }

  function getFullscreenElement() {
    return document.fullscreenElement || document.webkitFullscreenElement || null;
  }

  function requestNodeFullscreen(node) {
    if (node.requestFullscreen) {
      return node.requestFullscreen();
    }

    if (node.webkitRequestFullscreen) {
      return node.webkitRequestFullscreen();
    }

    return Promise.reject(new Error("Fullscreen API is unavailable."));
  }

  function exitBrowserFullscreen() {
    if (document.exitFullscreen) {
      return document.exitFullscreen();
    }

    if (document.webkitExitFullscreen) {
      return document.webkitExitFullscreen();
    }

    return Promise.resolve();
  }

  function roundRefreshRate(value) {
    if (!Number.isFinite(value) || value <= 0) {
      return 60;
    }

    const closest = COMMON_REFRESH_RATES.reduce((best, rate) => {
      return Math.abs(rate - value) < Math.abs(best - value) ? rate : best;
    }, COMMON_REFRESH_RATES[0]);

    if (Math.abs(closest - value) / closest <= 0.12) {
      return closest;
    }

    return Math.round(value);
  }

  function getStableRefreshRate(samples) {
    const sorted = samples
      .filter((delta) => delta >= 4 && delta <= 80)
      .sort((a, b) => a - b);

    if (sorted.length < 12) {
      return 60;
    }

    const fastSampleCount = Math.max(12, Math.floor(sorted.length * 0.38));
    const fastSamples = sorted.slice(0, fastSampleCount);
    const fastMedian = fastSamples[Math.floor(fastSamples.length / 2)];
    const stableFastSamples = fastSamples.filter((delta) => delta <= fastMedian * 1.18);
    const medianSource = stableFastSamples.length >= 8 ? stableFastSamples : fastSamples;
    const median = medianSource[Math.floor(medianSource.length / 2)];

    return roundRefreshRate(1000 / median);
  }

  function estimateRefreshRate() {
    return new Promise((resolve) => {
      const samples = [];
      let lastTimestamp = null;
      let attempts = 0;
      const startedAt = performance.now();

      function sample(timestamp) {
        attempts += 1;

        if (lastTimestamp !== null) {
          const delta = timestamp - lastTimestamp;

          if (delta > 0 && delta < 80) {
            samples.push(delta);
          }
        }

        lastTimestamp = timestamp;

        if (samples.length < 160 && attempts < 240 && timestamp - startedAt < 4200) {
          window.requestAnimationFrame(sample);
          return;
        }

        resolve(getStableRefreshRate(samples));
      }

      window.requestAnimationFrame(sample);
    });
  }

  function setupCanvas(canvas) {
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(320, rect.width || canvas.offsetWidth || 320);
    const height = Math.max(180, rect.height || canvas.offsetHeight || 180);
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const pixelWidth = Math.round(width * dpr);
    const pixelHeight = Math.round(height * dpr);

    if (canvas.width !== pixelWidth || canvas.height !== pixelHeight) {
      canvas.width = pixelWidth;
      canvas.height = pixelHeight;
    }

    const context = canvas.getContext("2d");
    context.setTransform(dpr, 0, 0, dpr, 0, 0);

    return { context, width, height };
  }

  function roundedRect(context, x, y, width, height, radius) {
    const safeRadius = Math.min(radius, width / 2, height / 2);

    context.beginPath();
    context.moveTo(x + safeRadius, y);
    context.lineTo(x + width - safeRadius, y);
    context.quadraticCurveTo(x + width, y, x + width, y + safeRadius);
    context.lineTo(x + width, y + height - safeRadius);
    context.quadraticCurveTo(x + width, y + height, x + width - safeRadius, y + height);
    context.lineTo(x + safeRadius, y + height);
    context.quadraticCurveTo(x, y + height, x, y + height - safeRadius);
    context.lineTo(x, y + safeRadius);
    context.quadraticCurveTo(x, y, x + safeRadius, y);
    context.closePath();
  }

  function clearCanvas(context, width, height, mode, background = "dark") {
    if (mode === "standard") {
      context.fillStyle = background === "white" ? "#f8fafc" : background === "black" ? "#050608" : "#808080";
    } else {
      context.fillStyle = background === "white" ? "#f6f7fb" : background === "gray" ? "#808080" : "#050608";
    }

    context.fillRect(0, 0, width, height);

    context.strokeStyle = mode === "standard" ? "rgba(17,24,39,0.08)" : "rgba(255,255,255,0.08)";
    context.lineWidth = 1;

    for (let x = 0; x < width; x += 42) {
      context.beginPath();
      context.moveTo(x, 0);
      context.lineTo(x, height);
      context.stroke();
    }

    for (let y = 0; y < height; y += 42) {
      context.beginPath();
      context.moveTo(0, y);
      context.lineTo(width, y);
      context.stroke();
    }
  }

  function drawMecha(context, x, y, scale, mode) {
    const accent = mode === "standard" ? "#2563eb" : "#e60012";
    const cyan = mode === "standard" ? "#0f766e" : "#35d5ff";
    const shell = mode === "standard" ? "#e5e7eb" : "#f7f8fb";
    const dark = mode === "standard" ? "#111827" : "#050608";

    context.save();
    context.translate(x, y);
    context.scale(scale, scale);

    context.fillStyle = "rgba(53, 213, 255, 0.18)";
    roundedRect(context, -18, 18, 168, 18, 6);
    context.fill();

    context.fillStyle = dark;
    roundedRect(context, 6, 8, 106, 46, 7);
    context.fill();

    context.fillStyle = shell;
    roundedRect(context, 14, 2, 82, 42, 6);
    context.fill();

    context.fillStyle = accent;
    roundedRect(context, 26, -8, 42, 18, 5);
    context.fill();

    context.fillStyle = cyan;
    roundedRect(context, 72, 11, 26, 9, 4);
    context.fill();

    context.fillStyle = dark;
    roundedRect(context, 26, 22, 40, 8, 4);
    context.fill();

    context.strokeStyle = accent;
    context.lineWidth = 5;
    context.beginPath();
    context.moveTo(18, 46);
    context.lineTo(1, 72);
    context.moveTo(54, 45);
    context.lineTo(43, 74);
    context.moveTo(91, 43);
    context.lineTo(118, 66);
    context.stroke();

    context.fillStyle = shell;
    roundedRect(context, -7, 70, 34, 12, 4);
    context.fill();
    roundedRect(context, 35, 72, 36, 12, 4);
    context.fill();
    roundedRect(context, 106, 62, 38, 12, 4);
    context.fill();

    context.fillStyle = dark;
    context.font = "800 14px Montserrat, Segoe UI, sans-serif";
    context.fillText("KTC", 31, 38);

    context.restore();
  }

  function drawStandardShape(context, x, y, width, height) {
    const gradient = context.createLinearGradient(x, y, x + width, y);
    gradient.addColorStop(0, "#2563eb");
    gradient.addColorStop(0.5, "#f8fafc");
    gradient.addColorStop(1, "#0f766e");

    context.fillStyle = "rgba(17, 24, 39, 0.16)";
    roundedRect(context, x - 10, y + height + 12, width + 20, 8, 4);
    context.fill();

    context.fillStyle = gradient;
    roundedRect(context, x, y, width, height, 7);
    context.fill();

    context.fillStyle = "#111827";
    context.beginPath();
    context.arc(x + width + 22, y + height / 2, height * 0.42, 0, Math.PI * 2);
    context.fill();
  }

  function setReadout(readout, title, copy) {
    if (!readout) {
      return;
    }

    const titleNode = readout.querySelector("strong");
    const copyNode = readout.querySelector("span");

    if (titleNode) {
      titleNode.textContent = title;
    }

    if (copyNode) {
      copyNode.textContent = copy;
    }
  }

  function getRefreshRating(hz) {
    if (hz >= 240) {
      return {
        label: "Esports-grade smoothness",
        copy: "Suitable for KTC H27E6 320Hz and other FPS-focused high refresh monitors."
      };
    }

    if (hz >= 144) {
      return {
        label: "High-refresh entry level",
        copy: "Strong enough for mainstream esports. Use the ghosting test to fine tune Overdrive."
      };
    }

    if (hz >= 60) {
      return {
        label: "Fine for office and media",
        copy: "Daily use is covered, but competitive gaming motion clarity is limited."
      };
    }

    return {
      label: "Below common display refresh",
      copy: "Check browser, cable, OS display settings, or consider a KTC high refresh monitor."
    };
  }

  function initMotionClarityTool(node) {
    if (!node || node.dataset.motionToolInitialized === "true") {
      return;
    }

    node.dataset.motionToolInitialized = "true";

    const canvases = {
      refresh: node.querySelector('[data-motion-canvas="refresh"]'),
      ghosting: node.querySelector('[data-motion-canvas="ghosting"]'),
      text: node.querySelector('[data-motion-canvas="text"]')
    };

    const elements = {
      fps: node.querySelector("[data-motion-fps]"),
      hz: node.querySelector("[data-motion-hz]"),
      inlineHz: node.querySelectorAll("[data-motion-hz-inline]"),
      mode: node.querySelector("[data-motion-current-mode]"),
      response: node.querySelector("[data-motion-response-rating]"),
      tabs: node.querySelectorAll("[data-motion-mode]"),
      summaries: node.querySelectorAll("[data-motion-summary]"),
      controlButtons: node.querySelectorAll("[data-motion-control-button]"),
      fullscreenButtons: node.querySelectorAll("[data-motion-fullscreen-button]"),
      refreshRateButtons: node.querySelectorAll("[data-motion-refresh-rate]"),
      refreshRateCustom: node.querySelector("[data-motion-refresh-custom]"),
      refreshResult: node.querySelector("[data-motion-refresh-result]"),
      ghostingResult: node.querySelector("[data-motion-ghosting-result]"),
      textResult: node.querySelector("[data-motion-text-result]"),
      ghostSpeed: node.querySelector("[data-motion-ghost-speed]"),
      ghostSpeedOutput: node.querySelector("[data-motion-ghost-speed-output]"),
      textSpeed: node.querySelector("[data-motion-text-speed]"),
      textSpeedOutput: node.querySelector("[data-motion-text-speed-output]"),
      summaryPanel: node.querySelector("[data-motion-summary-panel]"),
      summaryTitle: node.querySelector("[data-motion-summary-title]"),
      summaryCopy: node.querySelector("[data-motion-summary-copy]"),
      lowRefreshModal: node.querySelector("[data-motion-low-refresh-modal]"),
      lowHz: node.querySelector("[data-motion-low-hz]"),
      announcer: node.querySelector("[data-motion-announcer]")
    };

    const state = {
      mode: normalizeMode(getHashMode() || node.dataset.defaultMode),
      detectedHz: 60,
      lowRefreshThreshold: clamp(node.dataset.lowRefreshThreshold, 30, 120),
      lastTimestamp: performance.now(),
      fpsStartedAt: performance.now(),
      fpsFrames: 0,
      rafId: null,
      fullscreenModule: null,
      fullscreenScroll: null,
      refresh: {
        started: false,
        running: false,
        lanes: [],
        speed: 960,
        customHz: clamp(elements.refreshRateCustom?.value, 75, 500)
      },
      ghosting: {
        started: false,
        running: false,
        speed: clamp(elements.ghostSpeed?.value, 200, 1200),
        background: "gray",
        x: 0
      },
      text: {
        started: false,
        running: false,
        speed: clamp(elements.textSpeed?.value, 100, 2000),
        fontSize: 16,
        offset: 0
      }
    };

    function announce(message) {
      if (elements.announcer) {
        elements.announcer.textContent = message;
      }
    }

    function syncHzLabels() {
      const label = `${state.detectedHz}Hz`;

      if (elements.hz) {
        elements.hz.textContent = label;
      }

      elements.inlineHz.forEach((item) => {
        item.textContent = label;
      });
    }

    function syncControlButtons() {
      const labels = {
        refresh: {
          start: "Start Refresh Rate Demo",
          pause: "Pause Refresh Demo",
          resume: "Resume Refresh Demo"
        },
        ghosting: {
          start: "Start Ghosting Test",
          pause: "Pause Ghosting Test",
          resume: "Resume Ghosting Test"
        },
        text: {
          start: "Start Moving Text",
          pause: "Pause Moving Text",
          resume: "Resume Moving Text"
        }
      };

      elements.controlButtons.forEach((button) => {
        const moduleName = button.dataset.motionControlButton;
        const moduleState = state[moduleName];
        const moduleLabels = labels[moduleName];

        if (!moduleState || !moduleLabels) {
          return;
        }

        const paused = moduleState.started && !moduleState.running;

        if (!moduleState.started) {
          button.textContent = moduleLabels.start;
          button.setAttribute("aria-pressed", "false");
        } else if (moduleState.running) {
          button.textContent = moduleLabels.pause;
          button.setAttribute("aria-pressed", "true");
        } else if (paused) {
          button.textContent = moduleLabels.resume;
          button.setAttribute("aria-pressed", "false");
        }
      });
    }

    function getModuleNode(moduleName) {
      return node.querySelector(`[data-motion-module="${moduleName}"]`);
    }

    function isModuleFullscreen(moduleName) {
      const moduleNode = getModuleNode(moduleName);
      const fullscreenElement = getFullscreenElement();

      if (!moduleNode) {
        return false;
      }

      return state.fullscreenModule === moduleName
        || Boolean(fullscreenElement && (fullscreenElement === moduleNode || moduleNode.contains(fullscreenElement)));
    }

    function syncFullscreenButtons() {
      elements.fullscreenButtons.forEach((button) => {
        const moduleName = button.dataset.motionFullscreenButton;
        const active = isModuleFullscreen(moduleName);

        button.textContent = active ? "Exit Fullscreen" : "Fullscreen";
        button.setAttribute("aria-pressed", String(active));
      });
    }

    function rememberScrollPosition() {
      state.fullscreenScroll = {
        x: window.scrollX || window.pageXOffset || 0,
        y: window.scrollY || window.pageYOffset || 0
      };
    }

    function restoreScrollPosition() {
      const scroll = state.fullscreenScroll;

      if (!scroll) {
        return;
      }

      const restore = () => {
        window.scrollTo(scroll.x, scroll.y);
      };

      window.requestAnimationFrame(() => {
        restore();
        window.setTimeout(restore, 80);
        window.setTimeout(() => {
          restore();
          state.fullscreenScroll = null;
        }, 220);
      });
    }

    function setFallbackFullscreen(moduleName, active) {
      const moduleNode = getModuleNode(moduleName);

      if (!moduleNode) {
        return;
      }

      state.fullscreenModule = active ? moduleName : null;
      moduleNode.classList.toggle("is-motion-fullscreen", active);
      document.documentElement.classList.toggle("motion-clarity-module-fullscreen-active", active);
      document.body.classList.toggle("motion-clarity-module-fullscreen-active", active);
      syncFullscreenButtons();

      if (!active) {
        restoreScrollPosition();
      }
    }

    function exitModuleFullscreen() {
      if (state.fullscreenModule) {
        setFallbackFullscreen(state.fullscreenModule, false);
        return;
      }

      if (getFullscreenElement()) {
        Promise.resolve(exitBrowserFullscreen()).catch(() => {});
      }
    }

    function startModuleIfNeeded(moduleName) {
      if (moduleName === "refresh" && !state.refresh.started) {
        startRefresh(false);
      } else if (moduleName === "ghosting" && !state.ghosting.started) {
        startGhosting(false);
      } else if (moduleName === "text" && !state.text.started) {
        startText(false);
      }
    }

    function openModuleFullscreen(moduleName) {
      const moduleNode = getModuleNode(moduleName);

      if (!moduleNode) {
        return;
      }

      startModuleIfNeeded(moduleName);

      if (isModuleFullscreen(moduleName)) {
        return;
      }

      rememberScrollPosition();

      Promise.resolve(requestNodeFullscreen(moduleNode))
        .then(syncFullscreenButtons)
        .catch(() => {
          setFallbackFullscreen(moduleName, true);
        });
    }

    function toggleModuleFullscreen(moduleName) {
      if (isModuleFullscreen(moduleName)) {
        exitModuleFullscreen();
      } else {
        openModuleFullscreen(moduleName);
      }
    }

    function setupRefreshLanes() {
      state.refresh.speed = state.mode === "gaming"
        ? clamp(node.dataset.gamingDemoSpeed, 200, 1600)
        : clamp(node.dataset.standardDemoSpeed, 100, 800);

      const highHz = Math.round(clamp(state.refresh.customHz, 75, 500));
      const lanes = [
        { hz: highHz, label: `${highHz} FPS high refresh` },
        { hz: 60, label: "60 FPS" },
        { hz: 30, label: "30 FPS" },
        { hz: 15, label: "15 FPS" }
      ];

      state.refresh.lanes = lanes.map((lane, index) => {
        const existing = state.refresh.lanes[index] || {};

        return {
          hz: lane.hz,
          label: lane.label,
          x: existing.x || 0,
          lastUpdate: existing.lastUpdate || 0
        };
      });
    }

    function syncRefreshRateControls() {
      const activeHz = Math.round(clamp(state.refresh.customHz, 75, 500));

      elements.refreshRateButtons.forEach((button) => {
        const active = Number(button.dataset.motionRefreshRate) === activeHz;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      if (elements.refreshRateCustom && Number(elements.refreshRateCustom.value) !== activeHz) {
        elements.refreshRateCustom.value = String(activeHz);
      }
    }

    function setHighRefreshRate(value) {
      state.refresh.customHz = Math.round(clamp(value, 75, 500));
      setupRefreshLanes();
      syncRefreshRateControls();
      updateRefreshReadout();
      renderRefresh(performance.now());
      announce(`High refresh comparison row set to ${state.refresh.customHz} FPS.`);
    }

    function syncMode(updateHash = false) {
      node.classList.toggle("is-standard", state.mode === "standard");
      node.classList.toggle("is-gaming", state.mode === "gaming");
      document.documentElement.classList.toggle("motion-clarity-standard-boot", state.mode === "standard");

      elements.tabs.forEach((tab) => {
        const active = normalizeMode(tab.dataset.motionMode) === state.mode;
        tab.classList.toggle("is-active", active);
        tab.setAttribute("aria-selected", String(active));
      });

      elements.summaries.forEach((summary) => {
        summary.hidden = summary.dataset.motionSummary !== state.mode;
      });

      if (elements.mode) {
        elements.mode.textContent = state.mode === "gaming" ? "Gaming" : "Standard";
      }

      setupRefreshLanes();

      if (updateHash) {
        const nextHash = `#${state.mode}`;

        if (window.location.hash !== nextHash && window.history?.replaceState) {
          window.history.replaceState(null, "", `${window.location.pathname}${window.location.search}${nextHash}`);
        }
      }

      announce(`${state.mode === "gaming" ? "Gaming" : "Standard"} mode active.`);
    }

    function updateRefreshReadout() {
      const highHz = Math.round(clamp(state.refresh.customHz, 75, 500));

      setReadout(
        elements.refreshResult,
        `${highHz} FPS vs 60 FPS vs 30 FPS vs 15 FPS comparison.`,
        "Use the selectable top row as a high-refresh target, then compare it with the lower-FPS rows for stepping, smoothness, and pursuit tracking differences."
      );
    }

    function renderRefresh(now) {
      const canvas = canvases.refresh;

      if (!canvas) {
        return;
      }

      const { context, width, height } = setupCanvas(canvas);
      clearCanvas(context, width, height, state.mode);

      const laneCount = Math.max(1, state.refresh.lanes.length);
      const rowHeight = height / laneCount;
      const mechaScale = Math.min(0.86, Math.max(0.64, rowHeight / 132));
      const objectWidth = state.mode === "gaming" ? 168 * mechaScale : 132;
      const objectHeight = state.mode === "gaming" ? 92 * mechaScale : 34;

      context.save();

      state.refresh.lanes.forEach((lane, index) => {
        const top = rowHeight * index;
        const interval = 1000 / Math.max(1, lane.hz);

        if (state.refresh.running) {
          if (!lane.lastUpdate) {
            lane.lastUpdate = now;
            lane.x = width * 0.12;
          }

          if (now - lane.lastUpdate >= interval) {
            const elapsed = Math.min(0.15, (now - lane.lastUpdate) / 1000);
            lane.x = (lane.x + state.refresh.speed * elapsed) % (width + objectWidth + 54);
            lane.lastUpdate = now;
          }
        } else if (!state.refresh.started) {
          lane.x = width * 0.12;
          lane.lastUpdate = now;
        }

        context.save();
        context.beginPath();
        context.rect(0, top, width, rowHeight);
        context.clip();

        if (index > 0) {
          context.setLineDash([8, 9]);
          context.strokeStyle = state.mode === "standard" ? "rgba(17,24,39,0.22)" : "rgba(255,255,255,0.24)";
          context.beginPath();
          context.moveTo(0, top);
          context.lineTo(width, top);
          context.stroke();
          context.setLineDash([]);
        }

        context.fillStyle = state.mode === "standard" ? "#111827" : "#f7f8fb";
        context.font = "800 16px Montserrat, Segoe UI, sans-serif";
        context.fillText(lane.label, 18, top + 30);

        context.fillStyle = state.mode === "standard" ? "rgba(17,24,39,0.58)" : "rgba(247,248,251,0.62)";
        context.font = "700 12px Montserrat, Segoe UI, sans-serif";
        context.fillText(`${Math.round(state.refresh.speed)} px/s`, 18, top + 52);

        const drawX = lane.x - objectWidth;
        const drawY = top + Math.min(
          Math.max(16, rowHeight * 0.58 - objectHeight / 2),
          Math.max(12, rowHeight - objectHeight - 10)
        );

        if (state.mode === "gaming") {
          drawMecha(context, drawX, drawY, mechaScale, state.mode);
        } else {
          drawStandardShape(context, drawX, drawY, objectWidth, objectHeight);
        }

        context.restore();
      });

      if (!state.refresh.started) {
        context.fillStyle = state.mode === "standard" ? "rgba(17,24,39,0.72)" : "rgba(247,248,251,0.72)";
        context.font = "800 14px Montserrat, Segoe UI, sans-serif";
        context.textAlign = "center";
        context.fillText("Press Start Refresh Rate Demo", width / 2, height - 28);
        context.textAlign = "start";
      } else if (!state.refresh.running) {
        context.fillStyle = state.mode === "standard" ? "rgba(17,24,39,0.72)" : "rgba(247,248,251,0.72)";
        context.font = "800 14px Montserrat, Segoe UI, sans-serif";
        context.textAlign = "center";
        context.fillText("Refresh demo paused", width / 2, height - 28);
        context.textAlign = "start";
      }

      context.restore();
    }

    function renderGhosting(now, deltaSeconds) {
      const canvas = canvases.ghosting;

      if (!canvas) {
        return;
      }

      const { context, width, height } = setupCanvas(canvas);
      clearCanvas(context, width, height, state.mode, state.ghosting.background);

      const objectWidth = state.mode === "gaming" ? 146 : 118;
      const y = Math.max(68, height * 0.5 - 42);

      if (state.ghosting.running) {
        state.ghosting.x = (state.ghosting.x + state.ghosting.speed * deltaSeconds) % (width + objectWidth + 80);
      } else if (!state.ghosting.x) {
        state.ghosting.x = width * 0.5;
      }

      const x = state.ghosting.x - objectWidth;

      context.strokeStyle = state.ghosting.background === "white" ? "rgba(17,24,39,0.35)" : "rgba(255,255,255,0.35)";
      context.lineWidth = 2;
      context.beginPath();
      context.moveTo(0, height * 0.5 + 54);
      context.lineTo(width, height * 0.5 + 54);
      context.stroke();

      if (state.mode === "gaming") {
        drawMecha(context, x, y, 0.9, state.mode);
      } else {
        drawStandardShape(context, x, y + 30, objectWidth, 38);
      }

      context.fillStyle = state.ghosting.background === "white" ? "#111827" : "#f7f8fb";
      context.font = "800 13px Montserrat, Segoe UI, sans-serif";
      context.fillText(`${Math.round(state.ghosting.speed)} px/s`, 18, 30);
      context.fillText(`${state.ghosting.background.toUpperCase()} BACKGROUND`, 18, 52);
    }

    function renderText(deltaSeconds) {
      const canvas = canvases.text;

      if (!canvas) {
        return;
      }

      const { context, width, height } = setupCanvas(canvas);
      clearCanvas(context, width, height, state.mode);

      if (state.text.running) {
        state.text.offset = (state.text.offset + state.text.speed * deltaSeconds) % 5000;
      }

      const bandHeight = Math.max(76, state.text.fontSize * 4.6);
      const bandY = (height - bandHeight) / 2;
      const textY = bandY + bandHeight / 2 + state.text.fontSize / 2.8;

      context.fillStyle = state.mode === "standard" ? "#111827" : "#f7f8fb";
      roundedRect(context, 0, bandY, width, bandHeight, 0);
      context.fill();

      context.fillStyle = state.mode === "standard" ? "#f8fafc" : "#050608";
      context.font = `900 ${state.text.fontSize}px Montserrat, Segoe UI, sans-serif`;

      const text = `${TEXT_SAMPLE}   ${TEXT_SAMPLE}`;
      const textWidth = context.measureText(text).width;
      const gap = Math.max(60, state.text.fontSize * 4);
      const repeatWidth = textWidth + gap;
      let x = -(state.text.offset % repeatWidth);

      while (x < width + repeatWidth) {
        context.fillText(text, x, textY);
        x += repeatWidth;
      }

      context.strokeStyle = state.mode === "standard" ? "#2563eb" : "#7c3aed";
      context.lineWidth = 3;
      context.beginPath();
      context.moveTo(0, bandY + bandHeight + 8);
      context.lineTo(width, bandY + bandHeight + 8);
      context.stroke();

      context.fillStyle = state.mode === "standard" ? "#111827" : "#f7f8fb";
      context.font = "800 13px Montserrat, Segoe UI, sans-serif";
      context.fillText(`${state.text.fontSize}px / ${Math.round(state.text.speed)} px/s`, 18, 30);
    }

    function updateFps(now) {
      state.fpsFrames += 1;

      if (now - state.fpsStartedAt < 500) {
        return;
      }

      const fps = Math.round((state.fpsFrames * 1000) / (now - state.fpsStartedAt));

      if (elements.fps) {
        elements.fps.textContent = String(fps);
      }

      state.fpsFrames = 0;
      state.fpsStartedAt = now;
    }

    function renderLoop(now) {
      const deltaSeconds = Math.min(0.05, Math.max(0, (now - state.lastTimestamp) / 1000));
      state.lastTimestamp = now;

      updateFps(now);
      renderRefresh(now);
      renderGhosting(now, deltaSeconds);
      renderText(deltaSeconds);

      state.rafId = window.requestAnimationFrame(renderLoop);
    }

    function startRefresh(scrollToModule = false) {
      state.refresh.started = true;
      state.refresh.running = true;
      setupRefreshLanes();
      updateRefreshReadout();
      syncControlButtons();

      if (scrollToModule) {
        node.querySelector('[data-motion-module="refresh"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      announce("Refresh rate comparison started.");
    }

    function startGhosting(scrollToModule = false) {
      state.ghosting.started = true;
      state.ghosting.running = true;
      syncControlButtons();

      if (scrollToModule) {
        node.querySelector('[data-motion-module="ghosting"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      announce("Ghosting and Overdrive test started.");
    }

    function startText(scrollToModule = false) {
      state.text.started = true;
      state.text.running = true;
      syncControlButtons();

      if (scrollToModule) {
        node.querySelector('[data-motion-module="text"]')?.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      announce("Moving text test started.");
    }

    function controlModule(moduleName, source) {
      const moduleState = state[moduleName];

      if (!moduleState) {
        return;
      }

      if (!moduleState.started) {
        if (moduleName === "refresh") {
          startRefresh(!source?.closest('[data-motion-module="refresh"]'));
        } else if (moduleName === "ghosting") {
          startGhosting(!source?.closest('[data-motion-module="ghosting"]'));
        } else if (moduleName === "text") {
          startText(!source?.closest('[data-motion-module="text"]'));
        }

        return;
      }

      moduleState.running = !moduleState.running;

      if (moduleName === "refresh" && moduleState.running) {
        const now = performance.now();
        state.refresh.lanes.forEach((lane) => {
          lane.lastUpdate = now;
        });
      }

      syncControlButtons();
      announce(`${moduleName === "text" ? "Moving text" : moduleName === "ghosting" ? "Ghosting test" : "Refresh demo"} ${moduleState.running ? "resumed" : "paused"}.`);
    }

    function setGhostBackground(background) {
      state.ghosting.background = ["black", "gray", "white"].includes(background) ? background : "black";

      node.querySelectorAll("[data-motion-bg]").forEach((button) => {
        const active = button.dataset.motionBg === state.ghosting.background;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });
    }

    function setTrailResult(result) {
      const map = {
        excellent: {
          hud: "Excellent (<1ms MPRT)",
          title: "Excellent response: no visible trail.",
          copy: "This is the target for KTC H-Series pro esports use and fast FPS tracking."
        },
        good: {
          hud: "Good (1-3ms)",
          title: "Good response: short trail only.",
          copy: "Mainstream esports performance is covered. Fine tune Overdrive if the trail grows on gray backgrounds."
        },
        fair: {
          hud: "Average (3-5ms)",
          title: "Visible ghosting detected.",
          copy: "Casual play is acceptable, but FPS motion clarity may improve with stronger Overdrive or a faster 1ms GTG panel."
        },
        inverse: {
          hud: "OD too high",
          title: "Inverse ghosting detected.",
          copy: "A bright edge usually means Overdrive is too aggressive. Step OD down until the bright outline disappears."
        }
      };
      const activeResult = map[result] || map.good;

      if (elements.response) {
        elements.response.textContent = activeResult.hud;
      }

      setReadout(elements.ghostingResult, activeResult.title, activeResult.copy);

      node.querySelectorAll("[data-motion-trail]").forEach((button) => {
        const active = button.dataset.motionTrail === result;
        button.classList.toggle("is-active", active);
        button.setAttribute("aria-pressed", String(active));
      });

      announce(activeResult.title);
    }

    function updateTextReadout() {
      const speed = Math.round(state.text.speed);

      const copy = speed >= 1000
        ? "If this remains readable, the display is showing strong moving-text clarity for fast HUDs and rapid document scrolling."
        : speed >= 600
          ? "This is a practical daily-use range. Increase the speed until the text stops being cleanly readable."
          : "This lower-speed range is useful for baseline checks on 60Hz office, portable, or media displays.";

      setReadout(
        elements.textResult,
        `Current text speed: ${speed} px/s.`,
        copy
      );
    }

    function showLowRefreshModal() {
      if (!elements.lowRefreshModal) {
        return;
      }

      if (elements.lowHz) {
        elements.lowHz.textContent = String(state.detectedHz);
      }

      elements.lowRefreshModal.hidden = false;
      document.documentElement.classList.add("motion-clarity-modal-active");
      document.body.classList.add("motion-clarity-modal-active");
    }

    function closeModal() {
      if (elements.lowRefreshModal) {
        elements.lowRefreshModal.hidden = true;
      }

      document.documentElement.classList.remove("motion-clarity-modal-active");
      document.body.classList.remove("motion-clarity-modal-active");
    }

    function finishTest() {
      const rating = getRefreshRating(state.detectedHz);

      if (elements.summaryPanel) {
        elements.summaryPanel.hidden = false;
      }

      if (elements.summaryTitle) {
        elements.summaryTitle.textContent = `About ${state.detectedHz}Hz estimated: ${rating.label}.`;
      }

      if (elements.summaryCopy) {
        elements.summaryCopy.textContent = `${rating.copy} Moving text reference speed: ${Math.round(state.text.speed)} px/s.`;
      }

      elements.summaryPanel?.scrollIntoView({ behavior: "smooth", block: "center" });

      if (state.detectedHz <= state.lowRefreshThreshold) {
        showLowRefreshModal();
      }

      announce("Motion clarity result summary updated.");
    }

    function handleAction(action, source) {
      if (action === "start-refresh") {
        startRefresh(!source?.closest('[data-motion-module="refresh"]'));
        return;
      }

      if (action === "start-ghosting") {
        startGhosting(!source?.closest('[data-motion-module="ghosting"]'));
        return;
      }

      if (action === "start-text") {
        startText(!source?.closest('[data-motion-module="text"]'));
        return;
      }

      if (action === "control-refresh" || action === "toggle-refresh") {
        controlModule("refresh", source);
        return;
      }

      if (action === "control-ghosting" || action === "toggle-ghosting") {
        controlModule("ghosting", source);
        return;
      }

      if (action === "control-text" || action === "toggle-text") {
        controlModule("text", source);
        return;
      }

      if (action === "fullscreen-refresh") {
        toggleModuleFullscreen("refresh");
        return;
      }

      if (action === "fullscreen-ghosting") {
        toggleModuleFullscreen("ghosting");
        return;
      }

      if (action === "fullscreen-text") {
        toggleModuleFullscreen("text");
        return;
      }

      if (action === "finish") {
        finishTest();
        return;
      }

      if (action === "close-modal") {
        closeModal();
      }
    }

    node.addEventListener("click", (event) => {
      const modeButton = event.target.closest("[data-motion-mode]");

      if (modeButton && node.contains(modeButton)) {
        event.preventDefault();
        state.mode = normalizeMode(modeButton.dataset.motionMode);
        syncMode(true);
        updateRefreshReadout();
        return;
      }

      const bgButton = event.target.closest("[data-motion-bg]");

      if (bgButton && node.contains(bgButton)) {
        event.preventDefault();
        setGhostBackground(bgButton.dataset.motionBg);

        if (!state.ghosting.started) {
          startGhosting(false);
        }

        return;
      }

      const trailButton = event.target.closest("[data-motion-trail]");

      if (trailButton && node.contains(trailButton)) {
        event.preventDefault();
        setTrailResult(trailButton.dataset.motionTrail);
        return;
      }

      const fontButton = event.target.closest("[data-motion-font-size]");

      if (fontButton && node.contains(fontButton)) {
        event.preventDefault();
        state.text.fontSize = clamp(fontButton.dataset.motionFontSize, 12, 24);

        node.querySelectorAll("[data-motion-font-size]").forEach((button) => {
          const active = Number(button.dataset.motionFontSize) === state.text.fontSize;
          button.classList.toggle("is-active", active);
          button.setAttribute("aria-pressed", String(active));
        });

        if (!state.text.started) {
          startText(false);
        }

        updateTextReadout();
        return;
      }

      const refreshRateButton = event.target.closest("[data-motion-refresh-rate]");

      if (refreshRateButton && node.contains(refreshRateButton)) {
        event.preventDefault();
        setHighRefreshRate(refreshRateButton.dataset.motionRefreshRate);
        return;
      }

      const fullscreenTrigger = event.target.closest("[data-motion-fullscreen-trigger]");

      if (fullscreenTrigger && node.contains(fullscreenTrigger)) {
        const moduleName = fullscreenTrigger.dataset.motionFullscreenTrigger;

        if (!isModuleFullscreen(moduleName)) {
          openModuleFullscreen(moduleName);
        }

        return;
      }

      const actionButton = event.target.closest("[data-motion-action]");

      if (actionButton && node.contains(actionButton)) {
        event.preventDefault();
        handleAction(actionButton.dataset.motionAction, actionButton);
      }
    });

    elements.ghostSpeed?.addEventListener("input", () => {
      state.ghosting.speed = clamp(elements.ghostSpeed.value, 200, 1200);

      if (elements.ghostSpeedOutput) {
        elements.ghostSpeedOutput.textContent = String(Math.round(state.ghosting.speed));
      }

      if (!state.ghosting.started) {
        startGhosting(false);
      }
    });

    elements.refreshRateCustom?.addEventListener("change", () => {
      setHighRefreshRate(elements.refreshRateCustom.value);
    });

    elements.textSpeed?.addEventListener("input", () => {
      state.text.speed = clamp(elements.textSpeed.value, 100, 2000);

      if (elements.textSpeedOutput) {
        elements.textSpeedOutput.textContent = String(Math.round(state.text.speed));
      }

      if (!state.text.started) {
        startText(false);
      }

      updateTextReadout();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape") {
        if (state.fullscreenModule) {
          exitModuleFullscreen();
          return;
        }

        closeModal();
      }
    });

    const handleFullscreenChange = () => {
      if (!getFullscreenElement()) {
        state.fullscreenModule = null;
        restoreScrollPosition();
      }

      syncFullscreenButtons();
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);

    window.addEventListener("hashchange", () => {
      const hashMode = getHashMode();

      if (!hashMode || hashMode === state.mode) {
        return;
      }

      state.mode = hashMode;
      syncMode(false);
      updateRefreshReadout();
    });

    const resizeObserver = "ResizeObserver" in window
      ? new ResizeObserver(() => {
        renderRefresh(performance.now());
        renderGhosting(performance.now(), 0);
        renderText(0);
      })
      : null;

    Object.values(canvases).forEach((canvas) => {
      if (canvas && resizeObserver) {
        resizeObserver.observe(canvas);
      }
    });

    syncMode(false);
    setGhostBackground("gray");
    syncRefreshRateControls();
    setupRefreshLanes();
    syncHzLabels();
    updateRefreshReadout();
    syncControlButtons();
    syncFullscreenButtons();

    if (elements.ghostSpeedOutput) {
      elements.ghostSpeedOutput.textContent = String(Math.round(state.ghosting.speed));
    }

    if (elements.textSpeedOutput) {
      elements.textSpeedOutput.textContent = String(Math.round(state.text.speed));
    }

    updateTextReadout();

    state.rafId = window.requestAnimationFrame(renderLoop);

    estimateRefreshRate().then((hz) => {
      state.detectedHz = hz;
      setupRefreshLanes();
      syncHzLabels();
      updateRefreshReadout();
    });

    window.addEventListener(
      "pagehide",
      () => {
        if (state.rafId) {
          window.cancelAnimationFrame(state.rafId);
        }

        if (resizeObserver) {
          resizeObserver.disconnect();
        }

        document.removeEventListener("fullscreenchange", handleFullscreenChange);
        document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
        document.documentElement.classList.remove("motion-clarity-module-fullscreen-active");
        document.body.classList.remove("motion-clarity-module-fullscreen-active");
        closeModal();
      },
      { once: true }
    );
  }

  function initAll(root = document) {
    root.querySelectorAll("[data-motion-tool]").forEach(initMotionClarityTool);
  }

  initAll(document);
  document.addEventListener("shopify:section:load", (event) => initAll(event.target));
})();
